import { useState, type FormEvent, type ReactNode } from "react";
import { can } from "@sts/schemas";
import type { Me, OrganisationProfile } from "../api/types.js";
import { Async, Failure } from "../components/Async.jsx";
import { AgreementLinks } from "../components/AgreementLinks.jsx";
import { ExportCredentials } from "../components/ExportCredentials.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";

/**
 * The company itself: what goes on a contract, and who accepts one.
 *
 * WHY THIS SCREEN EXISTS AT ALL. These columns have been on `organisations`
 * since migration 0023 with a working API in front of them, and no way to
 * reach it. The consequence was not cosmetic: `doc-templates/contract.ts`
 * refuses to render a contract without the trader's geographical address —
 * correctly, because under the Consumer Contracts Regulations 2013 the
 * customer has to be told where to send a cancellation, and a cancellation
 * notice with nowhere to send it is not one. So the generator failed, and the
 * only way to fix it was to reach into the database by hand.
 *
 * WHAT A BLANK FIELD MEANS, which is the thing to get right here. Saving sends
 * only what was typed, and the API merges rather than replaces, so a profile
 * can be filled in over two sittings without the first wiping the second. The
 * exception worth reading twice is VAT: no VAT number means the company is NOT
 * VAT registered, which the contract states to the customer in as many words.
 * The screen says so beside the field rather than leaving somebody to wonder
 * whether they have forgotten something.
 */

const EMPTY: OrganisationProfile = {};

export function CompanyScreen(): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<{
    me: Me;
    profile: OrganisationProfile | null;
  }>(async () => {
    // `me` first, and the profile only if the role may read it. Asking anyway
    // would earn a 403 and put the generic failure panel in front of a
    // surveyor instead of the sentence telling them whose job this is.
    const me = await client.me();
    const mayRead = can({
      role: me.role,
      resource: "org-settings",
      action: "read",
    });
    return { me, profile: mayRead ? await client.organisationProfile() : null };
  }, [client]);

  return (
    <section>
      <h1>Company</h1>
      <Async state={state} what="your company details" reload={reload}>
        {(loaded) =>
          loaded.profile !== null ? (
            <CompanyForm
              profile={loaded.profile}
              /* The API is the authority: a role without the grant is refused
                 by `authorize` inside the service whatever this renders. The
                 check here only hides a form that could never save, so the
                 reader sees what they may do instead of a form that turns red. */
              mayEdit={can({
                role: loaded.me.role,
                resource: "org-settings",
                action: "update",
              })}
            />
          ) : (
            <div className="notice warn" role="alert">
              <p>
                Your company details are managed by an owner or an administrator.
              </p>
            </div>
          )
        }
      </Async>
    </section>
  );
}

function CompanyForm({
  profile,
  mayEdit,
}: {
  readonly profile: OrganisationProfile;
  readonly mayEdit: boolean;
}): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();
  const [form, setForm] = useState<OrganisationProfile>(profile ?? EMPTY);
  const [saved, setSaved] = useState(false);

  const field = (key: keyof OrganisationProfile) => ({
    value: form[key] ?? "",
    onChange: (e: { target: { value: string } }) => {
      setSaved(false);
      setForm((f) => ({ ...f, [key]: e.target.value }));
    },
    disabled: !mayEdit,
  });

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    await submit(async () => {
      // Only what has something in it. Sending "" would be asking the API to
      // store a blank, and a blank is not the same fact as "not set" — see the
      // VAT note below.
      const NUMERIC = new Set([
        "workmanshipYears",
        "depositPence",
        "durationWorkingDays",
      ]);
      const patch: Record<string, string | number> = {};
      for (const [k, v] of Object.entries(form)) {
        const text = typeof v === "number" ? String(v) : v;
        if (typeof text !== "string" || text.trim() === "") continue;
        // The API takes whole numbers for these, and a form only ever holds
        // text. Converted here, at the edge, rather than loosening the route.
        patch[k] = NUMERIC.has(k) ? Number(text.trim()) : text.trim();
      }
      await client.saveOrganisationProfile(patch as OrganisationProfile);
      setSaved(true);
    });
  };

  const missingAddress =
    (form.addressLine1 ?? "").trim() === "" || (form.postcode ?? "").trim() === "";
  const missingSignatory = (form.countersignatoryName ?? "").trim() === "";

  return (
    <form className="sectioned" onSubmit={(e) => void onSubmit(e)}>
      {error !== null ? <Failure error={error} what="saving your company details" /> : null}

      {missingAddress ? (
        <div className="notice warn" role="alert">
          <p>
            A contract cannot be produced until the address below is filled in.
            It is printed on the agreement and is where a customer sends a
            cancellation, so the law requires it to be there.
          </p>
        </div>
      ) : null}

      <h2>Who you are</h2>
      <div className="fields">
        <div className="field">
          <label htmlFor="company-number">Company number</label>
          <input id="company-number" type="text" {...field("companyNumber")} />
          <p className="hint small">As registered at Companies House.</p>
        </div>
        <div className="field">
          <label htmlFor="company-vat">VAT number</label>
          <input id="company-vat" type="text" {...field("vatNumber")} />
          <p className="hint small">
            Leave this empty if you are not VAT registered. The contract then
            tells the customer that no VAT is added to their price.
          </p>
        </div>
      </div>

      <h2>Where you trade from</h2>
      <p className="hint small">
        Printed on every contract, and the address a customer sends a
        cancellation to.
      </p>
      <div className="fields">
        <div className="field">
          <label htmlFor="company-address1">Address</label>
          <input id="company-address1" type="text" {...field("addressLine1")} />
        </div>
        <div className="field">
          <label htmlFor="company-address2">Address line 2</label>
          <input id="company-address2" type="text" {...field("addressLine2")} />
        </div>
        <div className="field">
          <label htmlFor="company-town">Town or city</label>
          <input id="company-town" type="text" {...field("town")} />
        </div>
        <div className="field">
          <label htmlFor="company-postcode">Postcode</label>
          <input id="company-postcode" type="text" {...field("postcode")} />
        </div>
        <div className="field">
          <label htmlFor="company-phone">Telephone</label>
          <input id="company-phone" type="text" {...field("phone")} />
        </div>
        <div className="field">
          <label htmlFor="company-email">Email</label>
          <input id="company-email" type="email" {...field("email")} />
          <p className="hint small">
            Customers can send a cancellation here, so it needs to be a mailbox
            somebody reads.
          </p>
        </div>
      </div>

      <h2>Who signs for the company</h2>
      <p className="hint small">
        When a customer signs an agreement, it is countersigned straight away in
        this person&rsquo;s name, on the company&rsquo;s behalf. It is recorded
        as applied automatically — nobody has to be at a desk for a customer to
        be able to sign.
      </p>
      {missingSignatory ? (
        <div className="notice warn" role="alert">
          <p>
            Until this is filled in, a customer opening a signing link will be
            refused at the last step. An agreement countersigned in nobody&rsquo;s
            name is not countersigned.
          </p>
        </div>
      ) : null}
      <div className="fields">
        <div className="field">
          <label htmlFor="company-signatory">Name</label>
          <input id="company-signatory" type="text" {...field("countersignatoryName")} />
        </div>
        <div className="field">
          <label htmlFor="company-signatory-role">Role</label>
          <input id="company-signatory-role" type="text" {...field("countersignatoryRole")} />
          <p className="hint small">For example, Managing Director.</p>
        </div>
      </div>

      <h2>Your own customer agreement</h2>
      <p className="hint small">
        A much smaller document than the contract of sale below: it asks only
        for a customer&rsquo;s name, email and address, and everything else is
        the wording you write here. <strong>Nothing is supplied for you.</strong>{" "}
        An agreement binds whoever signs it, so the words have to be yours and
        checked by whoever checks such things for you. Until this is filled in,
        the agreement cannot be produced at all.
      </p>
      <div className="field">
        <label htmlFor="d-agreement">What the agreement says</label>
        <textarea
          id="d-agreement"
          rows={10}
          placeholder="Leave a blank line between paragraphs."
          {...field("customerAgreementTerms")}
        />
        <p className="hint small">
          Laid out as you type it: a blank line starts a new paragraph.
        </p>
      </div>

      <AgreementLinks canEdit={mayEdit} />

      <ExportCredentials canEdit={mayEdit} />

      <h2>Standing contract terms</h2>
      <p className="hint small">
        The parts of a contract that are the same every time. Entered once
        here, they appear on every agreement without being retyped — and a job
        can still record something different where a customer has agreed it.
      </p>
      <div className="fields">
        <div className="field">
          <label htmlFor="d-cc-name">Consumer code</label>
          <input id="d-cc-name" type="text" {...field("consumerCodeName")} />
        </div>
        <div className="field">
          <label htmlFor="d-cc-no">Consumer code membership number</label>
          <input id="d-cc-no" type="text" {...field("consumerCodeMembership")} />
        </div>
        <div className="field">
          <label htmlFor="d-cc-adr">Dispute resolution</label>
          <input id="d-cc-adr" type="text" {...field("consumerCodeDispute")} />
        </div>
        <div className="field">
          <label htmlFor="d-venue">Where you usually agree contracts</label>
          <select id="d-venue" {...field("venue")}>
            <option value="">Not set</option>
            <option value="off-premises">At the customer&rsquo;s home</option>
            <option value="distance">At a distance</option>
            <option value="on-premises">At your own premises</option>
          </select>
          <p className="hint small">
            A starting point only. Where a contract was actually agreed decides
            the right to cancel, so a job can always say otherwise.
          </p>
        </div>
        <div className="field">
          <label htmlFor="d-wy">Workmanship guarantee (years)</label>
          <input id="d-wy" type="text" inputMode="numeric" {...field("workmanshipYears")} />
        </div>
        <div className="field">
          <label htmlFor="d-wb">Guarantee backed by</label>
          <input id="d-wb" type="text" {...field("workmanshipBackedBy")} />
        </div>
        <div className="field">
          <label htmlFor="d-mw">Manufacturer warranty</label>
          <input id="d-mw" type="text" {...field("manufacturerWarranty")} />
        </div>
        <div className="field">
          <label htmlFor="d-mr">Maintenance requirement</label>
          <input id="d-mr" type="text" {...field("maintenanceRequirement")} />
        </div>
        <div className="field">
          <label htmlFor="d-gs">Grant scheme</label>
          <input id="d-gs" type="text" {...field("grantScheme")} />
        </div>
        <div className="field">
          <label htmlFor="d-rc">Who pays to return goods on cancellation</label>
          <select id="d-rc" {...field("returnCostsBorneBy")}>
            <option value="">Not set</option>
            <option value="contractor">You do</option>
            <option value="customer">The customer does</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="d-dep">Usual deposit (pence)</label>
          <input id="d-dep" type="text" inputMode="numeric" {...field("depositPence")} />
          <p className="hint small">
            0 where the grant covers the work. &ldquo;None&rdquo; and
            &ldquo;not said&rdquo; are different answers on a contract.
          </p>
        </div>
        <div className="field">
          <label htmlFor="d-start">Usual start</label>
          <input id="d-start" type="text" {...field("expectedStart")} />
        </div>
        <div className="field">
          <label htmlFor="d-days">Usual duration (working days)</label>
          <input id="d-days" type="text" inputMode="numeric" {...field("durationWorkingDays")} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="d-gi">If a grant is not paid</label>
        <textarea id="d-gi" rows={4} {...field("grantIfNotPaid")} />
        <p className="hint small">
          A contract showing a grant without this cannot be produced — a grant
          named with no answer to &ldquo;what if it does not arrive&rdquo; is a
          dispute waiting to happen.
        </p>
      </div>

      {mayEdit ? (
        <p>
          <button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save company details"}
          </button>
          {saved ? <span className="small muted"> Saved.</span> : null}
        </p>
      ) : (
        <p className="hint small">
          Only an owner or an administrator can change these.
        </p>
      )}
    </form>
  );
}
